import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import AppContext from '../../../../context/AppContext';
import '../../../../assests/css/admin/BlogManagement/BlogManagement.css';

const CATEGORIES = ['Product Reviews', 'Business Tips', 'Technology', 'Marketing'];

const validateBlogForm = ({ title, content, selectedCategory, slug }) => {
  const errors = {};

  if (!title.trim()) errors.title = 'Title is required.';
  else if (title.trim().length < 5) errors.title = 'Title must be at least 5 characters.';
  else if (title.trim().length > 200) errors.title = 'Title cannot exceed 200 characters.';

  if (!content.trim()) errors.content = 'Content is required.';
  else if (content.trim().length < 20) errors.content = 'Content must be at least 20 characters.';

  if (!selectedCategory) errors.category = 'Please select a category.';

  if (slug && !/^[a-z0-9-]+$/.test(slug)) {
    errors.slug = 'Slug can only contain lowercase letters, numbers, and hyphens.';
  }

  return errors;
};

const ErrorMsg = ({ message }) =>
  message ? (
    <span style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
      {message}
    </span>
  ) : null;

const toolbarButtons = [
  { icon: 'B', title: 'Bold' },
  { icon: 'I', title: 'Italic' },
  { icon: 'U', title: 'Underline' },
  { divider: true },
  { icon: '≡', title: 'Unordered List' },
  { icon: '≣', title: 'Ordered List' },
  { icon: '"', title: 'Quote' },
  { divider: true },
  { icon: '🖼️', title: 'Image' },
  { icon: '🔗', title: 'Link' },
  { icon: '<>', title: 'Code' },
  { divider: true },
  { icon: '↶', title: 'Undo' },
  { icon: '↷', title: 'Redo' }
];

const imageUrl = (baseUrl, image) => {
  if (!image) {
    return 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=500&h=300&fit=crop';
  }
  if (image.startsWith('http://') || image.startsWith('https://')) return image;

  const serverUrl = (baseUrl || '').replace(/\/api\/?$/, '');
  return `${serverUrl}/storage/${image.replace(/^\/+/, '')}`;
};

const AddBlogCard = ({ onBack, onSave }) => {
  const { baseUrl } = useContext(AppContext);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, image: 'Only JPG, PNG, WEBP, or GIF images are allowed.' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB.' }));
      return;
    }

    setErrors(prev => ({ ...prev, image: '' }));
    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setFeaturedImage(reader.result);
    reader.readAsDataURL(file);
  };

  const validate = () => {
    const validationErrors = validateBlogForm({ title, content, selectedCategory, slug });
    setTouched({ title: true, content: true, category: true, slug: true });
    setErrors(validationErrors);
    return Object.keys(validationErrors).length === 0;
  };

  const createBlog = async (blogStatus) => {
    if (!validate()) return;

    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('content', content.trim());

    // IMPORTANT: database column is mlcategory.
    formData.append('category', selectedCategory);

    formData.append('meta_title', metaTitle);
    formData.append('meta_description', metaDescription);
    formData.append('slug', slug);
    formData.append('status', blogStatus);

    if (imageFile) formData.append('image', imageFile);

    try {
      setLoading(true);

      const response = await axios.post(
        `${baseUrl}/admin/blog/create`,
        formData
      );

      if (response.data?.success) {
        alert(blogStatus === 'published'
          ? 'Blog published successfully!'
          : 'Blog saved as draft!'
        );
        onSave?.();
        onBack?.();
      } else {
        alert(response.data?.message || 'Blog could not be created.');
      }
    } catch (error) {
      console.error('CREATE BLOG ERROR:', error.response?.data || error);
      alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="Blog-Blog-Admin-Blog-add-container">
      <div className="container">
        <div className="Blog-Blog-Admin-Blog-add-header">
          <div className="Blog-Blog-Admin-Blog-add-header-left">
            <button className="Blog-Blog-Admin-Blog-back-btn" onClick={onBack}>←</button>
            <div className="Blog-Blog-Admin-Blog-add-title-section">
              <h2>Create New Post</h2>
              <p>Drafts are saved automatically</p>
            </div>
          </div>
        </div>

        <div className="Blog-Blog-Admin-Blog-add-content">
          <div className="Blog-Blog-Admin-Blog-editor-section">
            <div className="Blog-Blog-Admin-Blog-title-input">
              <input
                type="text"
                placeholder="Enter Title Here"
                value={title}
                onChange={e => setTitle(e.target.value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, title: true }));
                  setErrors(validateBlogForm({ title, content, selectedCategory, slug }));
                }}
              />
              {touched.title && <ErrorMsg message={errors.title} />}
            </div>

            <div className="Blog-Blog-Admin-Blog-editor-card">
              <div className="Blog-Blog-Admin-Blog-toolbar">
                {toolbarButtons.map((btn, index) =>
                  btn.divider
                    ? <div key={index} className="Blog-Blog-Admin-Blog-toolbar-divider" />
                    : <button type="button" key={index} className="Blog-Blog-Admin-Blog-toolbar-btn" title={btn.title}>
                        {btn.icon}
                      </button>
                )}
              </div>

              <textarea
                className="Blog-Blog-Admin-Blog-editor-textarea"
                placeholder="Start writing your story..."
                value={content}
                onChange={e => setContent(e.target.value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, content: true }));
                  setErrors(validateBlogForm({ title, content, selectedCategory, slug }));
                }}
              />
              {touched.content && <ErrorMsg message={errors.content} />}
            </div>
          </div>

          <div className="Blog-Blog-Admin-Blog-sidebar">
            <div className="Blog-Blog-Admin-Blog-sidebar-card">
              <h3>FEATURED IMAGE</h3>
              <input
                type="file"
                id="imageUpload"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
              <label htmlFor="imageUpload">
                <div className="Blog-Blog-Admin-Blog-upload-area">
                  {featuredImage ? (
                    <img src={featuredImage} alt="Featured" style={{ width: '100%', borderRadius: '8px' }} />
                  ) : (
                    <>
                      <div className="Blog-Blog-Admin-Blog-upload-icon">📷</div>
                      <div className="Blog-Blog-Admin-Blog-upload-text">Upload Featured Image</div>
                    </>
                  )}
                </div>
              </label>
              <ErrorMsg message={errors.image} />
            </div>

            <div className="Blog-Blog-Admin-Blog-sidebar-card">
              <h3>CATEGORIES</h3>
              <div className="Blog-Blog-Admin-Blog-category-grid">
                {CATEGORIES.map(category => (
                  <button
                    type="button"
                    key={category}
                    className={`Blog-Blog-Admin-Blog-category-btn ${selectedCategory === category ? 'Blog-Blog-Admin-Blog-active' : ''}`}
                    onClick={() => {
                      setSelectedCategory(category);
                      setTouched(prev => ({ ...prev, category: true }));
                      setErrors(prev => ({ ...prev, category: '' }));
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>
              {touched.category && <ErrorMsg message={errors.category} />}
            </div>

            <div className="Blog-Blog-Admin-Blog-sidebar-card">
              <h3>SEO SETTINGS</h3>

              <label className="Blog-Blog-Admin-Blog-meta-label">META TITLE</label>
              <input
                type="text"
                className="Blog-Blog-Admin-Blog-seo-input"
                placeholder="SEO Title..."
                value={metaTitle}
                onChange={e => setMetaTitle(e.target.value)}
              />

              <label className="Blog-Blog-Admin-Blog-meta-label">META DESCRIPTION</label>
              <textarea
                className="Blog-Blog-Admin-Blog-seo-textarea"
                placeholder="Brief summary for search results..."
                value={metaDescription}
                onChange={e => setMetaDescription(e.target.value)}
              />

              <label className="Blog-Blog-Admin-Blog-meta-label">SLUG</label>
              <div className="Blog-Blog-Admin-Blog-slug-container">
                <span className="Blog-Blog-Admin-Blog-slug-prefix">domain.com/blog/</span>
                <input
                  type="text"
                  className="Blog-Blog-Admin-Blog-slug-input"
                  placeholder="your-post-url"
                  value={slug}
                  onChange={e => setSlug(e.target.value)}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, slug: true }));
                    setErrors(validateBlogForm({ title, content, selectedCategory, slug }));
                  }}
                />
              </div>
              {touched.slug && <ErrorMsg message={errors.slug} />}
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '32px',
          paddingBottom: '40px',
          borderTop: '1px solid #e5e7eb',
          paddingTop: '24px'
        }}>
          <button className="Blog-Blog-Admin-Blog-save-draft-btn" onClick={onBack} disabled={loading}>
            Cancel
          </button>
          <button className="Blog-Blog-Admin-Blog-save-draft-btn" onClick={() => createBlog('draft')} disabled={loading}>
            {loading ? 'Saving...' : 'Save Draft'}
          </button>
          <button className="Blog-Blog-Admin-Blog-publish-btn" onClick={() => createBlog('published')} disabled={loading}>
            📤 {loading ? 'Publishing...' : 'Publish Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditBlogCard = ({ blog, onBack, onSave }) => {
  const { baseUrl } = useContext(AppContext);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [metaTitle, setMetaTitle] = useState('');
  const [metaDescription, setMetaDescription] = useState('');
  const [slug, setSlug] = useState('');
  const [featuredImage, setFeaturedImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!blog) return;

    setTitle(blog.title || '');
    setContent(blog.content || '');

    // Works with the mapped frontend field.
    setSelectedCategory(blog.category || blog.mlcategory || '');
    setMetaTitle(blog.metaTitle || blog.meta_title || blog.title || '');
    setMetaDescription(blog.metaDescription || blog.meta_description || '');
    setSlug(blog.slug || '');
    setFeaturedImage(blog.image || null);
  }, [blog]);

  const handleImageUpload = e => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

    if (!allowedTypes.includes(file.type)) {
      setErrors(prev => ({ ...prev, image: 'Only JPG, PNG, WEBP, or GIF images are allowed.' }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors(prev => ({ ...prev, image: 'Image size must be less than 5MB.' }));
      return;
    }

    setErrors(prev => ({ ...prev, image: '' }));
    setImageFile(file);

    const reader = new FileReader();
    reader.onloadend = () => setFeaturedImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpdate = async () => {
    const validationErrors = validateBlogForm({ title, content, selectedCategory, slug });
    setTouched({ title: true, content: true, category: true, slug: true });
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;

    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('content', content.trim());
    formData.append('mlcategory', selectedCategory);
    formData.append('meta_title', metaTitle);
    formData.append('meta_description', metaDescription);
    formData.append('slug', slug);

    if (imageFile) formData.append('image', imageFile);

    try {
      setLoading(true);

      const response = await axios.post(
        `${baseUrl}/admin/blog/update/${blog.id}`,
        formData
      );

      if (response.data?.success) {
        alert('Blog updated successfully!');
        onSave?.();
        onBack?.();
      } else {
        alert(response.data?.message || 'Blog could not be updated.');
      }
    } catch (error) {
      console.error('UPDATE BLOG ERROR:', error.response?.data || error);
      alert(error.response?.data?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="Blog-Blog-Admin-Blog-edit-container">
      <div className="container">
        <div className="Blog-Blog-Admin-Blog-edit-header">
          <div className="Blog-Blog-Admin-Blog-edit-header-left">
            <button className="Blog-Blog-Admin-Blog-edit-back-btn" onClick={onBack}>←</button>
            <div className="Blog-Blog-Admin-Blog-edit-title-section">
              <h2>Edit Blog Post</h2>
              <p>Edit your blog and save the changes.</p>
            </div>
          </div>
        </div>

        <div className="Blog-Blog-Admin-Blog-edit-content">
          <div className="Blog-Blog-Admin-Blog-edit-editor-section">
            <div className="Blog-Blog-Admin-Blog-edit-title-input">
              <input
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, title: true }));
                  setErrors(validateBlogForm({ title, content, selectedCategory, slug }));
                }}
              />
              {touched.title && <ErrorMsg message={errors.title} />}
            </div>

            <div className="Blog-Blog-Admin-Blog-edit-editor-card">
              <div className="Blog-Blog-Admin-Blog-edit-toolbar">
                {toolbarButtons.map((btn, index) =>
                  btn.divider
                    ? <div key={index} className="Blog-Blog-Admin-Blog-edit-toolbar-divider" />
                    : <button type="button" key={index} className="Blog-Blog-Admin-Blog-edit-toolbar-btn" title={btn.title}>
                        {btn.icon}
                      </button>
                )}
              </div>

              <textarea
                className="Blog-Blog-Admin-Blog-edit-editor-textarea"
                value={content}
                onChange={e => setContent(e.target.value)}
                onBlur={() => {
                  setTouched(prev => ({ ...prev, content: true }));
                  setErrors(validateBlogForm({ title, content, selectedCategory, slug }));
                }}
              />
              {touched.content && <ErrorMsg message={errors.content} />}
            </div>
          </div>

          <div className="Blog-Blog-Admin-Blog-edit-sidebar">
            <div className="Blog-Blog-Admin-Blog-edit-sidebar-card">
              <h3>FEATURED IMAGE</h3>
              <input
                type="file"
                id="editImageUpload"
                accept="image/jpeg,image/png,image/webp,image/gif"
                style={{ display: 'none' }}
                onChange={handleImageUpload}
              />
              <label htmlFor="editImageUpload">
                {featuredImage ? (
                  <div className="Blog-Blog-Admin-Blog-edit-featured-image">
                    <img
                      src={featuredImage.startsWith?.('data:') || featuredImage.startsWith?.('http')
                        ? featuredImage
                        : imageUrl(baseUrl, featuredImage)}
                      alt="Featured"
                    />
                  </div>
                ) : (
                  <div className="Blog-Blog-Admin-Blog-edit-upload-area">
                    <div className="Blog-Blog-Admin-Blog-edit-upload-icon">📷</div>
                    <div className="Blog-Blog-Admin-Blog-edit-upload-text">Upload Featured Image</div>
                  </div>
                )}
              </label>
              <ErrorMsg message={errors.image} />
            </div>

            <div className="Blog-Blog-Admin-Blog-edit-sidebar-card">
              <h3>CATEGORIES</h3>
              <div className="Blog-Blog-Admin-Blog-edit-category-grid">
                {CATEGORIES.map(category => (
                  <button
                    type="button"
                    key={category}
                    className={`Blog-Blog-Admin-Blog-edit-category-btn ${selectedCategory === category ? 'Blog-Blog-Admin-Blog-active' : ''}`}
                    onClick={() => {
                      setSelectedCategory(category);
                      setTouched(prev => ({ ...prev, category: true }));
                      setErrors(prev => ({ ...prev, category: '' }));
                    }}
                  >
                    {category}
                  </button>
                ))}
              </div>
              {touched.category && <ErrorMsg message={errors.category} />}
            </div>

            <div className="Blog-Blog-Admin-Blog-edit-sidebar-card">
              <h3>SEO SETTINGS</h3>

              <label className="Blog-Blog-Admin-Blog-edit-meta-label">META TITLE</label>
              <input
                type="text"
                className="Blog-Blog-Admin-Blog-edit-seo-input"
                value={metaTitle}
                onChange={e => setMetaTitle(e.target.value)}
              />

              <label className="Blog-Blog-Admin-Blog-edit-meta-label">META DESCRIPTION</label>
              <textarea
                className="Blog-Blog-Admin-Blog-edit-seo-textarea"
                value={metaDescription}
                onChange={e => setMetaDescription(e.target.value)}
              />

              <label className="Blog-Blog-Admin-Blog-edit-meta-label">SLUG</label>
              <div className="Blog-Blog-Admin-Blog-edit-slug-container">
                <span className="Blog-Blog-Admin-Blog-edit-slug-prefix">domain.com/blog/</span>
                <input
                  type="text"
                  className="Blog-Blog-Admin-Blog-edit-slug-input"
                  value={slug}
                  onChange={e => setSlug(e.target.value)}
                  onBlur={() => {
                    setTouched(prev => ({ ...prev, slug: true }));
                    setErrors(validateBlogForm({ title, content, selectedCategory, slug }));
                  }}
                />
              </div>
              {touched.slug && <ErrorMsg message={errors.slug} />}
            </div>
          </div>
        </div>

        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '12px',
          marginTop: '32px',
          paddingBottom: '40px',
          borderTop: '1px solid #e5e7eb',
          paddingTop: '24px'
        }}>
          <button className="Blog-Blog-Admin-Blog-discard-btn" onClick={onBack} disabled={loading}>
            Cancel
          </button>
          <button className="Blog-Blog-Admin-Blog-update-btn" onClick={handleUpdate} disabled={loading}>
            🔄 {loading ? 'Updating...' : 'Update Post'}
          </button>
        </div>
      </div>
    </div>
  );
};

const BlogCards = ({ blogs, onEdit, onDelete, onToggleStatus }) => {
  const getCategoryClass = category => ({
    'Product Reviews': 'Blog-Blog-Admin-Blog-category-product',
    Marketing: 'Blog-Blog-Admin-Blog-category-marketing',
    Technology: 'Blog-Blog-Admin-Blog-category-technology',
    'Business Tips': 'Blog-Blog-Admin-Blog-category-business'
  }[category] || 'Blog-Blog-Admin-Blog-category-product');

  const getImageWrapperClass = category => ({
    'Product Reviews': 'category-product',
    Marketing: 'category-marketing',
    Technology: 'category-technology',
    'Business Tips': 'category-business'
  }[category] || 'category-product');

  const getInitials = name =>
    name ? name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U';

  return (
    <div className="row g-4">
      {blogs.map(blog => (
        <div key={blog.id} className="col-lg-4 col-md-6">
          <div className="Blog-Blog-Admin-Blog-card">
            <div className={`Blog-Blog-Admin-Blog-card-image-wrapper ${getImageWrapperClass(blog.category)}`}>
              <img
                src={blog.image}
                alt={blog.title}
                className="Blog-Blog-Admin-Blog-card-image"
                onError={e => {
                  e.currentTarget.src = 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=500&h=300&fit=crop';
                }}
              />
              <span className={`Blog-Blog-Admin-Blog-category-badge ${getCategoryClass(blog.category)}`}>
                {blog.category}
              </span>
            </div>

            <div className="Blog-Blog-Admin-Blog-card-body">
              <div className="Blog-Blog-Admin-Blog-card-meta">
                <span className="Blog-Blog-Admin-Blog-card-date">{blog.date}</span>
                <span className={`Blog-Blog-Admin-Blog-status-badge ${blog.status === 'Published'
                  ? 'Blog-Blog-Admin-Blog-status-published'
                  : 'Blog-Blog-Admin-Blog-status-draft'
                }`}>
                  {blog.status}
                </span>
              </div>

              <h3 className="Blog-Blog-Admin-Blog-card-title">{blog.title}</h3>

              <div className="Blog-Blog-Admin-Blog-card-footer">
                <div className="Blog-Blog-Admin-Blog-author-info">
                  <div className="Blog-Blog-Admin-Blog-author-avatar">
                    {getInitials(blog.author)}
                  </div>
                  <span className="Blog-Blog-Admin-Blog-author-name">{blog.author}</span>
                </div>

                <div className="Blog-Blog-Admin-Blog-card-actions">
                  <label className="Blog-Blog-Admin-Blog-toggle-switch">
                    <input
                      type="checkbox"
                      checked={blog.status === 'Published'}
                      onChange={() => onToggleStatus(blog.id, blog.status)}
                    />
                    <span className="Blog-Blog-Admin-Blog-toggle-slider"></span>
                  </label>

                  <button
                    type="button"
                    className="Blog-Blog-Admin-Blog-action-btn"
                    onClick={() => onEdit(blog)}
                    title="Edit"
                  >
                    ✏️
                  </button>

                  <button
                    type="button"
                    className="Blog-Blog-Admin-Blog-action-btn Blog-Blog-Admin-Blog-action-btn-delete"
                    onClick={() => onDelete(blog.id)}
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

const BlogManagement = () => {
  const { baseUrl } = useContext(AppContext);
  const [view, setView] = useState('list');
  const [blogToEdit, setBlogToEdit] = useState(null);
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = [
    { name: 'All', icon: '🎯' },
    { name: 'Product Reviews', icon: '⭐' },
    { name: 'Business Tips', icon: '💼' },
    { name: 'Technology', icon: '🚀' },
    { name: 'Marketing', icon: '📊' }
  ];

  const getAllBlogs = async () => {
    if (!baseUrl) return;

    try {
      setLoading(true);

      const response = await axios.get(`${baseUrl}/admin/blog/getAllBlogs`);

      if (response.data?.success) {
        const mapped = (response.data.data || []).map(blog => ({
          id: blog.id,
          title: blog.title || '',
          content: blog.content || '',
          // IMPORTANT: DB column is mlcategory.
          category: blog.mlcategory || blog.category || '',
          mlcategory: blog.mlcategory || '',
          date: blog.created_at
            ? new Date(blog.created_at).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
              }).toUpperCase()
            : '',
          // Backend now stores lowercase values.
          status: String(blog.status || 'draft').toLowerCase() === 'published'
            ? 'Published'
            : 'Draft',
          author: blog.author_name || 'Admin',
          image: imageUrl(baseUrl, blog.image),
          metaTitle: blog.meta_title || '',
          metaDescription: blog.meta_description || '',
          slug: blog.slug || ''
        }));

        setBlogs(mapped);
      } else {
        setBlogs([]);
      }
    } catch (error) {
      console.error('GET BLOGS ERROR:', error.response?.data || error);
      setBlogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getAllBlogs();
    // baseUrl is needed to build the API URL.
  }, [baseUrl]);

  const handleDelete = async id => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;

    try {
      const formData = new FormData();
      formData.append('id', id);

      const response = await axios.post(
        `${baseUrl}/admin/blog/deleteBlog`,
        formData
      );

      if (response.data?.success) {
        await getAllBlogs();
      } else {
        alert(response.data?.message || 'Could not delete.');
      }
    } catch (error) {
      console.error('DELETE BLOG ERROR:', error.response?.data || error);
      alert(error.response?.data?.message || 'Could not delete. Please try again.');
    }
  };

  const handleToggleStatus = async (blogId, currentStatus) => {
    const newStatus = currentStatus === 'Published' ? 'draft' : 'published';

    try {
      const formData = new FormData();
      formData.append('status', newStatus);

      const response = await axios.post(
        `${baseUrl}/admin/blog/update/${blogId}`,
        formData
      );

      if (response.data?.success) {
        await getAllBlogs();
      } else {
        alert(response.data?.message || 'Status could not be changed.');
      }
    } catch (error) {
      console.error('TOGGLE STATUS ERROR:', error.response?.data || error);
      alert(error.response?.data?.message || 'Status could not be changed.');
    }
  };

  const filteredBlogs = selectedCategory === 'All'
    ? blogs
    : blogs.filter(blog => blog.category === selectedCategory);

  const handleCreateNew = () => {
    setBlogToEdit(null);
    setView('add');
  };

  const handleBack = () => {
    setView('list');
    setBlogToEdit(null);
    getAllBlogs();
  };

  const handleEdit = blog => {
    setBlogToEdit(blog);
    setView('edit');
  };

  if (view === 'add') {
    return <AddBlogCard onBack={handleBack} onSave={handleBack} />;
  }

  if (view === 'edit' && blogToEdit) {
    return <EditBlogCard blog={blogToEdit} onBack={handleBack} onSave={handleBack} />;
  }

  return (
    <div className="Blog-Blog-Admin-Blog-management-container">
      <div className="container">
        <div className="Blog-Blog-Admin-Blog-header">
          <div className="Blog-Blog-Admin-Blog-header-content">
            <div className="Blog-Blog-Admin-Blog-header-title">
              <h1>Blog Management</h1>
              <p>Create, edit and organize your storefront's stories.</p>
            </div>

            <button
              type="button"
              className="Blog-Blog-Admin-Blog-create-btn"
              onClick={handleCreateNew}
            >
              <span style={{ fontSize: '20px' }}>➕</span> Create New Post
            </button>
          </div>
        </div>

        <div className="Blog-Blog-Admin-Blog-filters">
          <div className="Blog-Blog-Admin-Blog-filters-header">
            <span className="Blog-Blog-Admin-Blog-filters-icon">☰</span>
            <span className="Blog-Blog-Admin-Blog-filters-title">Filter by category:</span>
          </div>

          <div className="Blog-Blog-Admin-Blog-category-filters">
            {categories.map(cat => (
              <button
                type="button"
                key={cat.name}
                className={`Blog-Blog-Admin-Blog-category-filter-btn ${selectedCategory === cat.name
                  ? 'Blog-Blog-Admin-Blog-active'
                  : ''
                }`}
                onClick={() => setSelectedCategory(cat.name)}
              >
                <span>{cat.icon}</span> {cat.name}
              </button>
            ))}
          </div>
        </div>

        <div className="Blog-Blog-Admin-Blog-blogs-grid">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px' }}>
              Loading blogs...
            </div>
          ) : filteredBlogs.length > 0 ? (
            <BlogCards
              blogs={filteredBlogs}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleStatus={handleToggleStatus}
            />
          ) : (
            <div className="Blog-Blog-Admin-Blog-empty-state">
              <div className="Blog-Blog-Admin-Blog-empty-icon">📝</div>
              <h3 className="Blog-Blog-Admin-Blog-empty-title">No blogs found</h3>
              <p className="Blog-Blog-Admin-Blog-empty-text">
                Start creating amazing content for your audience
              </p>
              <button
                type="button"
                className="Blog-Blog-Admin-Blog-empty-btn"
                onClick={handleCreateNew}
              >
                Create Your First Post
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BlogManagement;
